/**
 * Objectifs reliés aux programmes / packs / SKILLCARDS (migration 0514).
 * PUR, testé, partagé app ⇄ marketing.
 *
 * Le périmètre d'un objectif « exercices » est calculé côté serveur
 * (`objective_scope`) : règle du type, ou contenu épinglé par l'admin. Ce module
 * porte les libellés, la règle « l'élève choisit un pack » et la conversion de la
 * progression (cartes maîtrisées / maîtrise moyenne) vers l'anneau de progression.
 */

/** Seuil de maîtrise d'une carte (miroir de PACK_MASTERED_THRESHOLD et du SQL). */
export const OBJECTIVE_MASTERED_THRESHOLD = 80;

export type ObjectiveScopeRule =
  | 'pinned'
  | 'chosen_pack'
  | 'current_pack'
  | 'warmup'
  | 'weak'
  | 'programme'
  | 'epreuves';

/** Libellé court de la règle (admin et page de l'objectif). */
export const OBJECTIVE_RULE_LABELS: Record<ObjectiveScopeRule, string> = {
  pinned: 'Contenu choisi pour cet objectif',
  chosen_pack: 'Le pack que tu as choisi',
  current_pack: 'Le pack du moment de ton programme',
  warmup: 'Les packs déjà commencés de ton année',
  weak: 'Tes SKILLCARDS à consolider',
  programme: 'Tout le programme de ton année',
  epreuves: 'Sujets d’examen',
};

/** Règle appliquée par type quand l'admin n'a rien épinglé (miroir de `objective_scope`). */
export function objectiveRuleForType(
  type: string,
  opts: { requiresPackSelection?: boolean; contentType?: string } = {},
): ObjectiveScopeRule {
  if (opts.contentType === 'epreuves') return 'epreuves';
  if (opts.requiresPackSelection || type === 'chapter_review') return 'chosen_pack';
  if (type === 'current_chapter') return 'current_pack';
  if (type === 'warmup') return 'warmup';
  if (type === 'weak_spots') return 'weak';
  return 'programme';
}

/** Explication de la règle d'un type, pour l'admin. */
export const OBJECTIVE_RULE_HELP: Record<ObjectiveScopeRule, string> = {
  pinned: 'Les SKILLCARDS épinglées ci-dessous (programme, packs, cartes).',
  chosen_pack: 'L’élève choisit un pack de son programme ; ses SKILLCARDS forment l’objectif.',
  current_pack:
    'Le pack du moment du programme de l’élève (selon le mois), par matière. Change tout seul au fil de l’année.',
  warmup:
    'Les SKILLCARDS des packs déjà commencés ; seuls les exercices fondamentaux sont proposés.',
  weak: 'Les SKILLCARDS du programme travaillées avec moins de 80 % de maîtrise et celles repérées par le diagnostic (sinon le pack du moment).',
  programme:
    'Toutes les SKILLCARDS des packs du programme en vigueur de l’élève, pour ses matières.',
  epreuves: 'Les sujets d’examen (épreuves) du niveau : pas de SKILLCARDS.',
};

/** Un objectif « exercices » à pack choisi qui n'a pas encore son pack. */
export function objectiveNeedsPack(o: {
  rule: ObjectiveScopeRule | string;
  selectedPackId: string | null;
}): boolean {
  return o.rule === 'chosen_pack' && !o.selectedPackId;
}

export interface ObjectiveCardsProgress {
  card_count: number;
  mastered_count: number;
  avg_mastery: number;
  hora: number;
}

/** Progression des cartes → anneau (complétion = cartes maîtrisées, maîtrise = moyenne). */
export function objectiveRingFromCards(p: ObjectiveCardsProgress | null | undefined): {
  completionPercent: number;
  masteryPercent: number;
} {
  if (!p || p.card_count <= 0) return { completionPercent: 0, masteryPercent: 0 };
  return {
    completionPercent: Math.round((p.mastered_count / p.card_count) * 100),
    masteryPercent: Math.max(0, Math.min(100, Math.round(p.avg_mastery))),
  };
}

/** « 12 / 30 SKILLCARDS maîtrisées ». */
export function objectiveCardsLabel(p: ObjectiveCardsProgress | null | undefined): string {
  if (!p || p.card_count === 0) return 'Aucune SKILLCARD pour l’instant';
  const plural = p.card_count > 1 ? 's' : '';
  return `${p.mastered_count} / ${p.card_count} SKILLCARD${plural.toUpperCase()} maîtrisée${plural}`;
}

/** Types proposés dans l'admin (le diagnostic, auto-assigné, n'y figure pas). */
export const OBJECTIVE_TYPE_OPTIONS = [
  { value: 'full_program', label: 'Tout le programme' },
  { value: 'chapter_review', label: 'Réviser un pack (choisi par l’élève)' },
  { value: 'current_chapter', label: 'Pack en cours' },
  { value: 'weak_spots', label: 'Mes faiblesses' },
  { value: 'warmup', label: 'Échauffement (packs commencés)' },
  { value: 'brevet_revision', label: 'Révision brevet' },
  { value: 'brevet_blanc', label: 'Brevet blanc' },
  { value: 'bac_revision', label: 'Révision bac' },
  { value: 'bac_blanc', label: 'Bac blanc' },
] as const;
